import {
  Fragment,
  useMemo,
  useState,
  useId,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle
} from "react";
import type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  ColumnResizeDirection,
  ExpandedState,
  PaginationState,
  RowSelectionState,
  SortingState
} from "@tanstack/react-table";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
  getExpandedRowModel,
  getFacetedUniqueValues
} from "@tanstack/react-table";
import { type DragEndEvent } from "@dnd-kit/core";

import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";

import { CatalystColors } from "@certa/catalyst/constants/styles";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { classNames } from "@certa/catalyst/utils/common";
import { Spinner } from "@certa/icons/components/Spinner";
import {
  Button,
  ButtonSizes,
  ButtonVariants
} from "@certa/catalyst/components/Button";

import { TPagination } from "./TPagination/TPagination";

import { InternalTableHeader } from "./InternalTableHeader/InternalTableHeader";
import { InternalTableDndContext } from "./InternalTableDnd/InternalTableDndContext";
import { InternalTableFooter } from "./InternalTableFooter/InternalTableFooter";
import { InternalTableRowDraggable } from "./InternalTableRow/InternalTableRowDraggable";

import { extractDataFromTableComponents } from "./utils/extractDataFromTableComponents";
import { customFilterFn, filterFunctions } from "./utils/filters";

import {
  EXPAND_ROW_COLUMN_ID,
  SELECT_ROW_COLUMN_ID,
  DEFAULT_PAGINATION_STATE,
  DEFAULT_CELL_PADDING,
  DEFAULT_ROW_SELECTION_CONFIG
} from "./Table.constants";

import type {
  TableProps,
  TableStateChangePayload,
  TableImperativeHandle
} from "./Table.types";

import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";

import styles from "./Table.module.css";
import { getTransformedPaddingWithCSSVariables } from "@certa/catalyst/layouts/utils";
import { useIntl } from "react-intl";

const columnResizeDirection: ColumnResizeDirection = "ltr";

const TableComponent = <TableData extends Record<string, any>>(
  props: TableProps<TableData>,
  ref: React.Ref<TableImperativeHandle>
) => {
  const {
    caption,
    cellPadding = DEFAULT_CELL_PADDING,
    canEnableAllRowsSelection = true,
    pagination: paginationFromProps = {
      isManual: true,
      state: DEFAULT_PAGINATION_STATE,
      totalCount: 0,
      onChange: () => {},
      canShowPagination: true
    },
    sorting = {
      enabled: true,
      isManual: true,
      onChange: () => {}
    },
    filtering = {
      enabled: true,
      isManual: true,
      onChange: () => {}
    },
    columnResizing = {
      enabled: false,
      mode: "onChange"
    },
    columnOrdering = {
      enabled: false,
      onChange: () => {}
    },
    columnPinning: columnPinningFromProps = {
      enabled: false,
      initial: {
        left: [],
        right: []
      },
      onChange: () => {}
    },
    rowSelection: rowSelectionFromProps = DEFAULT_ROW_SELECTION_CONFIG,
    rowExpansion = {
      enabled: false,
      onChange: () => {}
    },
    rowReordering = {
      enabled: false,
      onChange: () => {}
    },
    rowStyle = {},
    isLoading = false,
    emptyText: emptyTextFromProps,
    emptyAction,
    height,
    fetchMoreOnScrollEnd,
    onStateChange
  } = props;
  const intl = useIntl();
  const emptyText =
    emptyTextFromProps ??
    intl.formatMessage({
      id: "errorMessageInstances.noDataFound",
      defaultMessage: "No data found"
    });

  const isRowExpansionEnabled = rowExpansion.enabled ?? false;
  const isRowSelectionEnabled = rowSelectionFromProps.enabled ?? false;
  const isRowReorderingEnabled = rowReordering.enabled ?? false;
  const isFilteringEnabled = filtering.enabled ?? true;
  const isSortingEnabled = sorting.enabled ?? true;
  const isColumnResizingEnabled = columnResizing.enabled ?? false;
  const isColumnOrderingEnabled = columnOrdering.enabled ?? false;
  const isColumnPinningEnabled = columnPinningFromProps.enabled ?? false;

  const { data: tableData, columns: normalizedColumns } = useMemo(
    () => extractDataFromTableComponents<TableData>(props.children),
    [props.children]
  );

  // This is used to keep the current table state consistent while it is loading
  // to show the loader as overlay on the table.
  const currentStateRef = useRef<{
    data: TableData[];
    pagination: PaginationState;
    totalCount: number;
  }>({
    data: tableData,
    pagination: paginationFromProps.state ?? DEFAULT_PAGINATION_STATE,
    totalCount: paginationFromProps.totalCount ?? 0
  });

  useEffect(() => {
    if (!isLoading) {
      currentStateRef.current = {
        data: tableData,
        pagination: paginationFromProps.state ?? DEFAULT_PAGINATION_STATE,
        totalCount: paginationFromProps.totalCount ?? 0
      };

      setPagination({
        pageIndex: (paginationFromProps.state?.pageIndex ?? 1) - 1,
        pageSize: paginationFromProps.state?.pageSize ?? 10
      });
    }
  }, [
    isLoading,
    tableData,
    paginationFromProps.state,
    paginationFromProps.totalCount
  ]);

  const expandableContentBaseId = useId();

  const tableColumns = useMemo(() => {
    const columnsWithRowExpansion = [];
    const columnsWithRowSelection = [];

    if (isRowSelectionEnabled) {
      columnsWithRowSelection.push({
        id: SELECT_ROW_COLUMN_ID,
        accessorKey: SELECT_ROW_COLUMN_ID,
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        enableResizing: false,
        size: 60
      });
    }

    if (isRowExpansionEnabled) {
      columnsWithRowExpansion.push({
        id: EXPAND_ROW_COLUMN_ID,
        accessorKey: EXPAND_ROW_COLUMN_ID,
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        enableResizing: false,
        size: 60
      });
    }

    return [
      ...columnsWithRowSelection,
      ...columnsWithRowExpansion,
      ...normalizedColumns
    ];
  }, [isRowExpansionEnabled, isRowSelectionEnabled, normalizedColumns]);

  // State variables
  // Simple internal column filters state
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Imperative handle for external control - consumer handles format conversion
  useImperativeHandle(
    ref,
    () => ({
      updateColumnFilters: (filters: ColumnFiltersState) => {
        if (filtering.isManual) {
          setColumnFilters(filters);
        }
      },
      updateColumnSorting: (sort: SortingState) => {
        if (sorting.isManual) {
          setColumnSorting(sort);
        }
      }
    }),
    [filtering.isManual, sorting.isManual]
  );
  const [columnSorting, setColumnSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>(() => ({
    pageIndex: (paginationFromProps.state?.pageIndex ?? 1) - 1,
    pageSize: paginationFromProps.state?.pageSize ?? 10
  }));
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    rowSelectionFromProps.selectedRows ?? {}
  );
  const [rowExpanded, setRowExpanded] = useState<ExpandedState>({});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() =>
    tableColumns.map(c => c.id)
  );

  useEffect(() => {
    setColumnOrder(tableColumns.map(c => c.id));
  }, [tableColumns]);

  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    // select column is pinned by default because it is used for row selection
    // and should always be visible
    left: [
      SELECT_ROW_COLUMN_ID,
      EXPAND_ROW_COLUMN_ID,
      ...(columnPinningFromProps.initial?.left ?? [])
    ],
    right: [...(columnPinningFromProps.initial?.right ?? [])]
  });

  const notifyStateChange = (trigger: TableStateChangePayload["trigger"]) => {
    if (!onStateChange) return;

    onStateChange({
      trigger,
      state: {
        sorting: columnSorting,
        filtering: columnFilters,
        pagination,
        columnOrder,
        columnPinning,
        rowSelection,
        expandedRows: rowExpanded
      }
    });
  };

  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState)
  ) => {
    const newValue =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnSorting)
        : updaterOrValue;
    setColumnSorting(newValue);
    sorting?.onChange?.(newValue);
    notifyStateChange("sorting");
  };

  const handleFilterChange = (
    updaterOrValue:
      | ColumnFiltersState
      | ((old: ColumnFiltersState) => ColumnFiltersState)
  ) => {
    const newValue =
      typeof updaterOrValue === "function"
        ? updaterOrValue(columnFilters)
        : updaterOrValue;
    setColumnFilters(newValue);
    filtering?.onChange?.(newValue);
    notifyStateChange("filtering");
  };

  const handlePageChange = (page: number) => {
    if (paginationFromProps.isManual) {
      paginationFromProps.onChange?.(page);
    } else {
      tableInstance.setPageIndex(page - 1);
    }
    notifyStateChange("pagination");
  };

  useEffect(() => {
    if (rowSelectionFromProps.selectedRows) {
      setRowSelection(rowSelectionFromProps.selectedRows);
    }
  }, [rowSelectionFromProps.selectedRows]);

  const tableInstance = useReactTable({
    data: currentStateRef.current.data,
    sortDescFirst: false,
    columns: tableColumns.map(column => ({
      ...column,
      // This custom function is required especially to ignore the sub rows from filtering
      // as it is breaking due to our custom sub (expand) row implementation
      filterFn: column.meta?.filterType
        ? filterFunctions[column.meta.filterType]
        : customFilterFn
    })),
    enableColumnResizing: isColumnResizingEnabled,
    enableColumnPinning: isColumnPinningEnabled,
    enableRowSelection: isRowSelectionEnabled
      ? row => rowSelectionFromProps.canSelect?.(row.original) ?? true
      : false,
    enableMultiRowSelection: rowSelectionFromProps.isMultiple ?? true,
    enableExpanding: isRowExpansionEnabled,
    columnResizeMode: columnResizing.mode ?? "onChange",
    columnResizeDirection,
    enableColumnFilters: isFilteringEnabled,
    enableSorting: isSortingEnabled,
    onExpandedChange: setRowExpanded,
    // The getSubRows function is used to get the sub rows for the expanded row
    // This is used to render the expanded row content
    getSubRows: row => row?.subExpandedRow,
    // The getRowId function is used to get the unique id for the row
    getRowId: (originalRow, index, parent) => originalRow?.id,
    // debugTable: true,
    // debugHeaders: true,
    // debugColumns: true,
    state: {
      columnFilters,
      sorting: columnSorting,
      //no need to pass pageCount or rowCount with client-side pagination as it is calculated automatically
      pagination,
      columnOrder,
      columnPinning,
      rowSelection,
      expanded: rowExpanded
    },
    onSortingChange: handleSortingChange,
    onRowSelectionChange: setRowSelection,
    onColumnFiltersChange: handleFilterChange,
    manualFiltering: filtering.isManual ?? true,
    getFilteredRowModel: getFilteredRowModel(),
    filterFns: filterFunctions,
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualExpanding: true,
    getExpandedRowModel: getExpandedRowModel(),
    // Row model - https://tanstack.com/table/latest/docs/guide/row-models
    getCoreRowModel: getCoreRowModel(),
    manualSorting: sorting.isManual ?? true,
    getSortedRowModel: getSortedRowModel(),
    onColumnPinningChange: setColumnPinning,
    manualPagination: paginationFromProps.isManual ?? true,
    getPaginationRowModel: getPaginationRowModel(), //load client-side pagination code
    onPaginationChange: setPagination
    // autoResetPageIndex: false, // turn off page index reset when sorting or filtering
  });

  const currentColumnResizeDirection =
    tableInstance.options.columnResizeDirection ?? "ltr";

  const currentColumnResizeMode =
    tableInstance.options.columnResizeMode ?? "onChange";

  const handleToggleColumnPin = (columnId: string, isPinned: boolean) => {
    const currentPinnedColumns = columnPinning.left ?? [];
    const pinnedColumns = isPinned
      ? [...currentPinnedColumns, columnId]
      : currentPinnedColumns.filter(column => column !== columnId);
    columnPinningFromProps.onChange?.(pinnedColumns);
    notifyStateChange("columnPinning");
  };

  // reorder columns after drag & drop
  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id.toString());
      const newIndex = columnOrder.indexOf(over.id.toString());
      const newColumnOrder = arrayMove(columnOrder, oldIndex, newIndex); //this is just a splice utility
      setColumnOrder(newColumnOrder);
      columnOrdering.onChange?.(newColumnOrder);
      notifyStateChange("columnOrder");
    }
  };

  // reorder rows after drag & drop
  const handleRowDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      // Find indices in the original data array, not the filtered/sorted view
      const originalData = currentStateRef.current.data;
      const oldIndex = originalData.findIndex(item => item.id === active.id);
      const newIndex = originalData.findIndex(item => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedData = arrayMove(originalData, oldIndex, newIndex);

        rowReordering.onChange?.(oldIndex, newIndex, reorderedData);
        notifyStateChange("rowReordering");
      }
    }
  };

  // Handle drag end - either columns or rows (never both)
  const handleDragEnd = (event: DragEndEvent) => {
    if (isRowReorderingEnabled) {
      handleRowDragEnd(event);
    } else {
      handleColumnDragEnd(event);
    }
  };

  const onRowSelect = (rowId: string) => {
    // Manually updating the current row selection state instead of using the state directly
    // because the state is not updated yet when the row selection checkbox is clicked.
    // If multi-row selection is disabled, only keep the current selection
    if (!rowSelectionFromProps.isMultiple) {
      const newRowSelection = {
        [rowId]: !rowSelection[rowId]
      };

      // If the row is being deselected, clear the selection
      if (!newRowSelection[rowId]) {
        const selectedRowsData: TableData[] = [];
        rowSelectionFromProps.onChange?.([], selectedRowsData);
        return;
      }

      // If the row is being selected, only keep this row selected
      const selectedRowData = [tableInstance.getRow(rowId).original];
      rowSelectionFromProps.onChange?.([rowId], selectedRowData);
      return;
    }

    // Multi-row selection logic
    const curentRowSelection: Record<string | number, boolean> = {
      ...rowSelection
    };
    if (curentRowSelection[rowId]) {
      delete curentRowSelection[rowId];
    } else {
      curentRowSelection[rowId] = true;
    }
    const selectedRowsData = Object.keys(curentRowSelection).map(rowId => {
      try {
        return tableInstance.getRow(rowId).original;
      } catch (error) {
        return null;
      }
    });

    rowSelectionFromProps.onChange?.(
      Object.keys(curentRowSelection),
      selectedRowsData
    );
    notifyStateChange("rowSelection");
  };

  const onSelectAllRows = (rowIds: string[]) => {
    const selectedRowsData = rowIds.map(rowId => {
      return tableInstance.getRow(rowId).original;
    });
    rowSelectionFromProps.onChange?.(rowIds, selectedRowsData);
    notifyStateChange("rowSelection");
  };

  const onRowExpand = (rowId: string) => {
    const currentRowExpansion: Record<string | number, boolean> = {
      ...(rowExpanded as Record<string | number, boolean>)
    };
    if (currentRowExpansion[rowId]) {
      delete currentRowExpansion[rowId];
    } else {
      currentRowExpansion[rowId] = true;
    }
    rowExpansion.onChange?.(rowId, Object.keys(currentRowExpansion));
    notifyStateChange("rowExpansion");
  };

  const tableInstanceRows = tableInstance.getRowModel().rows;

  const { scrollableContainerRef: tableContainerRef, handleScroll } =
    useInfiniteScroll(fetchMoreOnScrollEnd);

  return (
    <InternalTableDndContext
      onDragEnd={handleDragEnd}
      restrictAxis={isRowReorderingEnabled ? "vertical" : undefined}
    >
      <Stack
        overflow="auto"
        dangerouslySetClassName={
          styles.tableWrapper +
          // Chrome's browser translation feature is causing the table to crash.
          // This is a workaround to disable the translation feature to avoid the crash.
          " notranslate"
        }
      >
        <div
          ref={tableContainerRef}
          className={classNames({
            [styles.tableContainer]: true,
            [styles.tableContainerFixedHeight]: tableInstanceRows.length === 0
          })}
          style={
            {
              overflow: tableInstanceRows.length > 0 ? "auto" : "hidden",
              height,
              "--table-cell-padding":
                getTransformedPaddingWithCSSVariables(cellPadding)
            } as React.CSSProperties
          }
          onScroll={handleScroll}
        >
          {isLoading && (
            <div className={styles.tableLoadingContainer}>
              <Spinner
                color={CatalystColors.CERTA_BRAND_400}
                size={24}
                data-testid="table-loading-spinner"
              />
            </div>
          )}
          <table className={styles.table}>
            <caption
              className={classNames({
                "catalyst-screen-reader-only": true,
                [styles.tableCaption]: true
              })}
            >
              {caption}
            </caption>
            <thead className={styles.tableHeader}>
              {tableInstance.getHeaderGroups().map((headerGroup, index) => (
                <tr
                  key={`${headerGroup.id}-${index}`}
                  className={styles.tableRow}
                >
                  {isRowReorderingEnabled && (
                    <th className={styles.reorderHeaderCell}>
                      <span className="catalyst-screen-reader-only">
                        Reorder
                      </span>
                    </th>
                  )}
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header, index) => {
                      return (
                        <InternalTableHeader<TableData>
                          key={`${header.id}-${index}`}
                          header={header}
                          columnResizeDirection={currentColumnResizeDirection}
                          columnResizeMode={currentColumnResizeMode}
                          isManualFiltering={filtering.isManual ?? true}
                          columnSizingInfo={
                            tableInstance.getState().columnSizingInfo
                          }
                          canEnableColumnOrdering={isColumnOrderingEnabled}
                          canEnableMultiRowSelection={
                            rowSelectionFromProps.isMultiple ?? true
                          }
                          onToggleColumnPin={handleToggleColumnPin}
                          onRowSelect={onSelectAllRows}
                          canEnableAllRowsSelection={canEnableAllRowsSelection}
                        />
                      );
                    })}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            {tableInstanceRows.length > 0 ? (
              <>
                <tbody className={styles.tableBody}>
                  <SortableContext
                    items={tableInstanceRows.map(row => row.id)}
                    strategy={verticalListSortingStrategy}
                    disabled={!isRowReorderingEnabled}
                  >
                    {tableInstanceRows.map((row, index) => {
                      const expandableRowId = `${expandableContentBaseId}-${row.id}`;
                      const canDragRow =
                        rowReordering.canDrag?.(row.original) ?? true;
                      const customRowClassName = rowStyle.getRowClassName?.(
                        row.original
                      );

                      return (
                        <InternalTableRowDraggable
                          key={`${row.id}-${index}`}
                          row={row}
                          columnOrder={columnOrder}
                          expandableRowId={expandableRowId}
                          onRowSelect={onRowSelect}
                          onRowExpand={onRowExpand}
                          isReorderingEnabled={isRowReorderingEnabled}
                          canDrag={canDragRow}
                          tableInstance={tableInstance}
                          rowClassName={customRowClassName}
                          canShowSelectionColumn={
                            rowSelectionFromProps.showSelectionColumn
                          }
                        />
                      );
                    })}
                  </SortableContext>
                </tbody>
                {/* TODO: For table with fixed height, footer should stick to the bottom of the table */}
                {tableColumns.some(column => !!column.footer) && (
                  <tfoot className={styles.tableFooter}>
                    {tableInstance
                      .getFooterGroups()
                      .map((footerGroup, index) => (
                        <tr
                          key={`${footerGroup.id}-${index}`}
                          className={styles.tableRow}
                        >
                          <SortableContext
                            items={columnOrder}
                            strategy={horizontalListSortingStrategy}
                          >
                            {footerGroup.headers.map((header, index) => {
                              return (
                                <InternalTableFooter
                                  key={`${header.id}-${index}`}
                                  header={header}
                                />
                              );
                            })}
                          </SortableContext>
                        </tr>
                      ))}
                  </tfoot>
                )}
              </>
            ) : (
              !isLoading && (
                <div className={styles.tableEmptyContainer}>
                  {emptyText}
                  {emptyAction && (
                    <Button
                      size={ButtonSizes.SMALL}
                      onClick={emptyAction.onClick}
                      variant={ButtonVariants.OUTLINE}
                      disabled={emptyAction.disabled}
                      loading={emptyAction.loading}
                    >
                      {emptyAction.text}
                    </Button>
                  )}
                </div>
              )
            )}
          </table>
        </div>
        {/* NO NEED TO SHOW PAGINATION IF INFINITE SCROLL IS ENABLED */}
        {!fetchMoreOnScrollEnd &&
        tableInstanceRows.length > 0 &&
        (paginationFromProps.canShowPagination ?? true) ? (
          <div className={styles.tablePagination}>
            <TPagination
              currentPage={
                paginationFromProps.isManual
                  ? currentStateRef.current.pagination.pageIndex
                  : pagination.pageIndex + 1
              }
              onPageChange={handlePageChange}
              totalCount={
                paginationFromProps.isManual
                  ? currentStateRef.current.totalCount
                  : tableData.length
              }
              pageSize={pagination.pageSize}
            />
          </div>
        ) : null}
      </Stack>
    </InternalTableDndContext>
  );
};

export const Table = forwardRef(TableComponent) as <
  TableData extends Record<string, any>
>(
  props: TableProps<TableData> & { ref?: React.Ref<TableImperativeHandle> }
) => React.ReactElement;
